'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import { getAuth } from '@/lib/adapters';
import type { AuthUser, AuthSession } from '@/lib/adapters/interfaces';
import { ROLE_PERMISSIONS, ROLE_LABELS, hasPermission } from '@/lib/rbac/permissions';
import type { UserRole, Permission } from '@/lib/rbac/permissions';

export type { UserRole };
export { ROLE_LABELS, ROLE_PERMISSIONS };

export const AuthContext = createContext<AuthContextValue>({} as AuthContextValue);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<UserRole>('viewer');
  const [teamId, setTeamId] = useState<string | null>(null);

  const auth = getAuth();

  const fetchRoleAndTeam = async (userId: string) => {
    try {
      const { getDatabase } = await import('@/lib/adapters');
      const row = await getDatabase().selectOne<any>('user_profiles', {
        filters: [{ column: 'id', operator: 'eq', value: userId }],
      });
      if (row) {
        setRole((row.role as UserRole) || 'viewer');
        setTeamId(row.team_id || null);
      }
    } catch {
      // silently fall back to viewer
    }
  };

  useEffect(() => {
    auth.getSession().then(async (s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user?.id) {
        await fetchRoleAndTeam(s.user.id);
      }
      setLoading(false);
    });

    const unsubscribe = auth.onAuthStateChange(async (_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user?.id) {
        await fetchRoleAndTeam(s.user.id);
      } else {
        setRole('viewer');
        setTeamId(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshRole = async () => {
    if (user?.id) {
      await fetchRoleAndTeam(user.id);
    }
  };

  const hasPermissionFn = (permission: Permission): boolean => {
    return hasPermission(role, permission);
  };

  const signUp = async (email: string, password: string, metadata: Record<string, unknown> = {}) => {
    const data = await auth.signUp(email, password, {
      metadata: {
        full_name: (metadata as any)?.fullName || '',
        avatar_url: (metadata as any)?.avatarUrl || '',
        role: (metadata as any)?.role || 'viewer',
      },
      redirectTo: typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : undefined,
    });
    return data;
  };

  const signIn = async (email: string, password: string) => {
    return auth.signIn(email, password);
  };

  const signOut = async () => {
    await auth.signOut();
    setRole('viewer');
    setTeamId(null);
  };

  const getCurrentUser = async () => {
    return auth.getUser();
  };

  const isEmailVerified = () => {
    return user?.emailConfirmedAt !== null && user?.emailConfirmedAt !== undefined;
  };

  const getUserProfile = async () => {
    if (!user) return null;
    const { getDatabase } = await import('@/lib/adapters');
    const row = await getDatabase().selectOne('user_profiles', {
      filters: [{ column: 'id', operator: 'eq', value: user.id }],
    });
    return row ?? null;
  };

  const value: AuthContextValue = {
    user,
    session,
    loading,
    role,
    teamId,
    hasPermission: hasPermissionFn,
    signUp,
    signIn,
    signOut,
    getCurrentUser,
    isEmailVerified,
    getUserProfile,
    refreshRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
